import React, { useState } from 'react';
import {
  useGetExternalItemMapsQuery,
  useGetItemsDropdownQuery,
  useGetItemsQuery,
  usePatchExternalItemMapsByIdMutation,
} from '../../../redux/vmsApi';
import {
  Box, Typography, TextField, Stack, Button, TableContainer,
  Paper, Table, TableHead, TableRow, TableCell, TableBody,
  Select, MenuItem, FormControl,
  DialogActions,
  DialogContent,
  Dialog,
  DialogTitle,
  Grid,
  Autocomplete
} from '@mui/material';
import { camelCaseToSpaced } from '../../../utils/formattingUtil';

const ExternalItemMappingListComponent: React.FC = () => {
  const { data: inventoryItems = [] } = useGetItemsDropdownQuery();
  const [filters, setFilters] = useState({
    itemCode: "",
    description: "",
    inventoryCode: "",
    categoryCode: "",
    categoryDescription: "",
  });

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [apiParams, setApiParams] = useState({ ...filters, skip: 0, limit: 10 });

  const { data: items, isLoading, isError } = useGetExternalItemMapsQuery(apiParams);
  const [updateItemMap] = usePatchExternalItemMapsByIdMutation();

  const [open, setOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    setApiParams({ ...filters, skip: 0, limit: pageSize });
    setPage(0);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setApiParams((prev) => ({ ...prev, skip: newPage * pageSize }));
  };

  const handlePageSizeChange = (event: any) => {
    const newSize = Number(event.target.value);
    setPageSize(newSize);
    setPage(0);
    setApiParams((prev) => ({ ...prev, limit: newSize, skip: 0 }));
  };

  const handleOpenModal = (item: any) => {
    setSelectedItem(item);
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
    setSelectedItem(null);
  };

  const handleSave = async () => {
    if (!selectedItem) return;
    try {
      await updateItemMap({
        id: selectedItem.id,
        body: {
          itemCode: selectedItem.itemCode,
          description: selectedItem.description,
          quantityUnit: selectedItem.quantityUnit,
        },
      }).unwrap();
      handleCloseModal();
    } catch (err) {
      console.error("Error updating item map:", err);
    }
  };

  const handleAutocompleteChange = (newValue: any) => {
    if (newValue) {
      setSelectedItem((prev: any) => ({
        ...prev,
        itemCode: newValue.itemCode || "",
        description: newValue.description || "",
        quantityUnit: newValue.quantityUnit || "",
      }));
    }
  };


  if (isLoading) return <div>Loading...</div>;
  if (isError) return <div>Error fetching items.</div>;

  return (
    <React.Fragment>
      
      <Box sx={{ padding: 3, borderRadius: 2, marginBottom: 3, backgroundColor: "#f9f9f9", boxShadow: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
          External Items List
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={2}>
          {["itemCode", "description", "inventoryCode", "categoryCode", "categoryDescription"].map((field) => (
            <TextField
              key={field}
              label={field.replace(/([A-Z])/g, " $1")}
              variant="outlined"
              size="small"
              value={(filters as any)[field]}
              onChange={(e) => handleFilterChange(field, e.target.value)}
              sx={{ maxWidth: 240, backgroundColor: "#fff", borderRadius: 1 }}
              fullWidth
            />
          ))}
        </Box>
        <Stack direction="row" spacing={2} mt={2}>
          <Button
            variant="outlined"
            size="large"
            onClick={handleApplyFilters}
            sx={{
              textTransform: "none",
              fontWeight: "bold",
              color: "#1976d2",
              borderColor: "#1976d2",
              "&:hover": { backgroundColor: "#e3f2fd", borderColor: "#1565c0" },
            }}
          >
            Apply Filters
          </Button>
        </Stack>
      </Box>

     
      <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2 }}>
        <Table>
          <TableHead sx={{ backgroundColor: "#1976d2" }}>
            <TableRow>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>External Item Code</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>Item Code</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>External Description</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>Description</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>Quantity Unit</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items && items.length > 0 ? (
              items.map((item: any, index: number) => (
                <TableRow key={item.id} sx={{ backgroundColor: index % 2 === 0 ? "#f0f4f7" : "white" }}>
                  <TableCell>{item.externalItemCode}</TableCell>
                  <TableCell>{item.itemCode}</TableCell>
                  <TableCell>{item.externalDescription}</TableCell>
                  <TableCell>{item.description}</TableCell>
                  <TableCell>{item.quantityUnit}</TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      size="small"
                      sx={{
                        backgroundColor: "#1976d2",
                        textTransform: "none",
                        borderRadius: 2,
                        "&:hover": { backgroundColor: "#1565c0" },
                      }}
                      onClick={() => handleOpenModal(item)}
                    >
                      Map
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center">No matching items found.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      
      <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
        <Button
          onClick={() => handlePageChange(Math.max(page - 1, 0))}
          disabled={page === 0}
          variant="contained"
          sx={{ marginRight: 2 }}
        >
          Previous
        </Button>
        <Typography variant="body1">Page {page + 1}</Typography>
        <Button
          onClick={() => handlePageChange(page + 1)}
          disabled={items?.length === undefined || items.length < pageSize}
          variant="contained"
          sx={{ marginLeft: 2 }}
        >
          Next
        </Button>
      </Box>

      <Box mt={2} display="flex" justifyContent="center" alignItems="center">
        <Typography variant="body2" sx={{ marginRight: 2 }}>Rows per page:</Typography>
        <FormControl variant="outlined" size="small">
          <Select value={pageSize} onChange={handlePageSizeChange} sx={{ backgroundColor: "#f0f4f7" }}>
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
          </Select>
        </FormControl>
      </Box>

    
      <Dialog
        open={open}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            padding: 2,
            background: "linear-gradient(135deg, #ffffff 30%, #f9f9f9 90%)",
            boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: "bold", color: "#1976d2", textAlign: "center" }}>
          Edit Item Mapping
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} mt={2}>
          
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Item Code"
                  value={selectedItem?.itemCode || ""}
                  onChange={(e) =>
                    setSelectedItem((prev: any) => ({ ...prev, itemCode: e.target.value }))
                  }
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="External Item Code"
                  value={selectedItem?.externalItemCode || ""}
                  onChange={(e) =>
                    setSelectedItem((prev: any) => ({
                      ...prev,
                      externalItemCode: e.target.value,
                    }))
                  }
                  fullWidth
                />
              </Grid>
            </Grid>

           
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Autocomplete
                  options={inventoryItems}
                  getOptionLabel={(option) => option.description || ""}
                  value={
                    inventoryItems.find((inv) => inv.itemCode === selectedItem?.itemCode) || null
                  }
                  onChange={(event, newValue) => handleAutocompleteChange(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search Inventory Item"
                      variant="outlined"
                      sx={{ backgroundColor: "#fff", borderRadius: 1 }}
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label="External Description"
                  value={selectedItem?.externalDescription || ""}
                  onChange={(e) =>
                    setSelectedItem((prev: any) => ({
                      ...prev,
                      externalDescription: e.target.value,
                    }))
                  }
                  fullWidth
                />
              </Grid>
            </Grid>


            <TextField
              label="Quantity Unit"
              value={selectedItem?.quantityUnit || ""}
              onChange={(e) =>
                setSelectedItem((prev: any) => ({
                  ...prev,
                  quantityUnit: e.target.value,
                }))
              }
              fullWidth
            />
          </Stack>

        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseModal}
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: "none",
              borderColor: "#1976d2",
              color: "#1976d2",
              "&:hover": { backgroundColor: "#e3f2fd", borderColor: "#1565c0" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            sx={{
              borderRadius: 2,
              textTransform: "none",
              backgroundColor: "#1976d2",
              "&:hover": { backgroundColor: "#1565c0" },
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default ExternalItemMappingListComponent;






